/**
 * Terminal Display Switch Service
 * Manages terminal visibility display flags
 */

import { getCurrentConfig, getApiScope } from '../config';

class TerminalDisplayService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  /**
   * Get display flag status for a terminal
   */
  async getDisplayFlag(terminalId: number): Promise<boolean> {
    const baseUrl = this.getBaseURL();
    const url = `${baseUrl}/displayswitch/${terminalId}`;

    // Get access token
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()],
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      console.error('Failed to get token:', error);
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data; // Returns boolean
  }

  /**
   * Toggle display flag for a terminal
   */
  async toggleDisplayFlag(
    terminalId: number, 
    updatedBy: string = 'API',
    reason?: string
  ): Promise<boolean> {
    const baseUrl = this.getBaseURL();
    let url = `${baseUrl}/displayswitch/${terminalId}/toggle?updated_by=${encodeURIComponent(updatedBy)}`;
    
    if (reason) {
      url += `&reason=${encodeURIComponent(reason)}`;
    }

    // Get access token
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()],
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      console.error('Failed to get token:', error);
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data; // Returns new boolean value
  }
}

// Export singleton instance
export const terminalDisplayService = new TerminalDisplayService();
export default terminalDisplayService;
